import { defaultError } from "@-/com.npmjs.react-context-default-error";
import { createContext, useContext, useLayoutEffect, useRef } from "react";
import { StickyAccordionBottomFadeContext } from "#sticky-accordion-bottom-fade-context";
import type {
  StickyAccordionHeaderProps,
  StickyAccordionViewportProps,
} from "#sticky-accordion-types";

type Header = {
  anchor: HTMLDivElement;
  element: HTMLDivElement;
  index: number;
  key: string;
  open: boolean;
  toggle: () => void;
};
type Coordinator = {
  activate: (key: string) => void;
  register: (header: Header) => () => void;
};
const SCROLL_OVERFLOW = /auto|scroll/;
/** Provided by `StickyAccordionViewport`; every `StickyAccordionHeader` renders inside one. */
const CoordinatorContext = createContext(defaultError<Coordinator>("CoordinatorContext"));

/** Browsers own sticky positioning in their scrolling engine, without a scroll-event transform. */
export function StickyAccordionViewport({ borderColor, children }: StickyAccordionViewportProps) {
  const { gap, side } = useContext(StickyAccordionBottomFadeContext);
  const root = useRef<HTMLDivElement>(null);
  const headers = useRef(new Map<string, Header>());
  const refresh = useRef(() => {});
  const pending = useRef<{ edge: "top" | "bottom"; key: string } | null>(null);
  const scrollElement = useRef<HTMLElement | null>(null);
  const ordered = () => [...headers.current.values()].sort((a, b) => a.index - b.index);
  const naturalY = (header: Header, scroll: HTMLElement) =>
    header.anchor.getBoundingClientRect().top -
    scroll.getBoundingClientRect().top +
    scroll.scrollTop;
  const scrollToHeader = (header: Header, edge: "top" | "bottom") => {
    const scroll = scrollElement.current;
    if (!scroll) {
      return;
    }
    const list = ordered();
    const index = list.indexOf(header);
    const before = list.slice(0, index).reduce((sum, item) => sum + item.element.offsetHeight, 0);
    const after = list.slice(index + 1).reduce((sum, item) => sum + item.element.offsetHeight, 0);
    const next = list[index + 1];
    const target =
      edge === "bottom"
        ? naturalY(header, scroll) - before
        : (next ? naturalY(next, scroll) : scroll.scrollHeight) -
          Math.max(0, scroll.clientHeight - after);
    scroll.scrollTo({
      behavior: "smooth",
      top: Math.max(0, Math.min(target, scroll.scrollHeight - scroll.clientHeight)),
    });
  };
  useLayoutEffect(() => {
    const element = root.current;
    if (!element) {
      return;
    }
    const scroll = [...element.querySelectorAll<HTMLElement>("div")].find((node) =>
      SCROLL_OVERFLOW.test(getComputedStyle(node).overflowY),
    );
    if (!scroll) {
      return;
    }
    scrollElement.current = scroll;
    const update = () => {
      const list = ordered();
      const total = list.reduce((sum, header) => sum + header.element.offsetHeight, 0);
      let before = 0;
      for (const header of list) {
        const height = header.element.offsetHeight;
        header.element.style.top = `${before}px`;
        header.element.style.bottom = `${total - before - height}px`;
        before += height;
      }
      // Only the fade uses scroll notifications. Header movement is entirely CSS sticky.
      const height = scroll.clientHeight;
      let top = 0;
      let topCount = 0;
      for (const header of list) {
        if (naturalY(header, scroll) - scroll.scrollTop > top) {
          break;
        }
        top += header.element.offsetHeight;
        topCount += 1;
      }
      let bottom = 0;
      for (const header of list.slice(topCount).reverse()) {
        if (
          naturalY(header, scroll) - scroll.scrollTop <
          height - bottom - header.element.offsetHeight
        ) {
          break;
        }
        bottom += header.element.offsetHeight;
      }
      const hasBody = height > top + bottom && scroll.scrollHeight > height + 1;
      const showTop = hasBody && scroll.scrollTop > 1;
      const showBottom = hasBody && scroll.scrollTop < scroll.scrollHeight - height - 1;
      element.style.setProperty("--han-top-alpha", showTop ? "0" : "1");
      element.style.setProperty("--han-bottom-alpha", showBottom ? "0" : "1");
      const bands = [
        `linear-gradient(to bottom, black 0px, black ${top}px, rgb(0 0 0 / var(--han-top-alpha)) ${top}px, black ${top + 4}px, black ${Math.max(top + 4, height - bottom - 4)}px, rgb(0 0 0 / var(--han-bottom-alpha)) ${height - bottom}px, black ${height - bottom}px, black 100%)`,
      ];
      const bandSizes = ["100% 100%"];
      const bandPositions = ["0 0"];
      if (side !== "full") {
        // Restore opaque pixels beside the connected tab, only within the bottom fade band.
        bands.push("linear-gradient(black, black)");
        bandSizes.push(`calc(50% + ${gap / 2}px) 4px`);
        bandPositions.push(`${side === "left" ? "right" : "left"} 0 top ${height - bottom - 4}px`);
      }
      const viewportTop = scroll.getBoundingClientRect().top;
      let hasTopBorder = false;
      let hasBottomBorder = false;
      for (const header of list) {
        const rect = header.element.getBoundingClientRect();
        const y = rect.top - viewportTop;
        bands.push(
          `linear-gradient(to bottom, transparent ${y}px, black ${y}px, black ${y + rect.height}px, transparent ${y + rect.height}px)`,
        );
        bandSizes.push("100% 100%");
        bandPositions.push("0 0");
        const native = naturalY(header, scroll) - scroll.scrollTop;
        // A naturally flush header blends into the toolbar until scrolling displaces it.
        const firstTop: boolean = y > native + 0.5 && !hasTopBorder;
        const firstBottom: boolean = y < native - 0.5 && !hasBottomBorder;
        hasTopBorder ||= firstTop;
        hasBottomBorder ||= firstBottom;
        header.element.style.boxShadow =
          firstTop || firstBottom ? `inset 0 1px ${borderColor}` : "none";
      }
      element.style.maskImage = bands.join(",");
      element.style.maskSize = bandSizes.join(",");
      element.style.maskPosition = bandPositions.join(",");
      element.style.maskRepeat = "no-repeat";
      if (pending.current) {
        const header = headers.current.get(pending.current.key);
        if (!header) {
          pending.current = null;
        } else if (header.open) {
          const { edge } = pending.current;
          pending.current = null;
          scrollToHeader(header, edge);
        }
      }
    };
    refresh.current = update;
    const observer = new ResizeObserver(update);
    observer.observe(scroll);
    if (scroll.firstElementChild) {
      observer.observe(scroll.firstElementChild);
    }
    for (const header of headers.current.values()) {
      observer.observe(header.element);
    }
    const cancelPending = () => {
      pending.current = null;
    };
    const cancelOnDrag = (event: PointerEvent) => {
      if (event.buttons !== 0) {
        cancelPending();
      }
    };
    scroll.addEventListener("pointermove", cancelOnDrag, { passive: true });
    scroll.addEventListener("wheel", cancelPending, { passive: true });
    scroll.addEventListener("scroll", update, { passive: true });
    update();
    return () => {
      observer.disconnect();
      scroll.removeEventListener("scroll", update);
      scroll.removeEventListener("pointermove", cancelOnDrag);
      scroll.removeEventListener("wheel", cancelPending);
      scrollElement.current = null;
      refresh.current = () => {};
    };
  });
  const coordinator: Coordinator = {
    activate(key) {
      const header = headers.current.get(key);
      const scroll = scrollElement.current;
      if (!header || !scroll || (pending.current?.key === key && !header.open)) {
        return;
      }
      const natural = naturalY(header, scroll) - scroll.scrollTop;
      const actual =
        header.element.getBoundingClientRect().top - scroll.getBoundingClientRect().top;
      const edge = actual > natural + 0.5 ? "top" : actual < natural - 0.5 ? "bottom" : null;
      if (!edge) {
        pending.current = null;
        header.toggle();
      } else if (header.open) {
        pending.current = null;
        scrollToHeader(header, edge);
      } else {
        pending.current = { edge, key };
        header.toggle();
      }
    },
    register(header) {
      headers.current.set(header.key, header);
      // React cleans up sibling layout effects before registering their replacements.
      // Resolve only after that commit has finished registering the entire header list.
      queueMicrotask(() => refresh.current());
      return () => {
        headers.current.delete(header.key);
      };
    },
  };
  return (
    <CoordinatorContext value={coordinator}>
      <style>{`
        @property --han-top-alpha { syntax: '<number>'; inherits: false; initial-value: 1; }
        @property --han-bottom-alpha { syntax: '<number>'; inherits: false; initial-value: 1; }
        [data-han-sticky] { transition: --han-top-alpha 160ms, --han-bottom-alpha 160ms; }
      `}</style>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden" data-han-sticky ref={root}>
        {children}
      </div>
    </CoordinatorContext>
  );
}

export function StickyAccordionHeader({
  actions = [],
  actionTrailingInset = 0,
  actionWidth = 0,
  busy,
  children,
  disabled,
  headerKey,
  index,
  label,
  onAction,
  onToggle,
  open,
}: StickyAccordionHeaderProps) {
  const coordinator = useContext(CoordinatorContext);
  const anchor = useRef<HTMLDivElement>(null);
  const element = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!anchor.current || !element.current) {
      return;
    }
    return coordinator.register({
      anchor: anchor.current,
      element: element.current,
      index,
      key: headerKey,
      open,
      toggle: onToggle,
    });
  }, [coordinator, headerKey, index, onToggle, open]);
  return (
    <>
      <div aria-hidden className="h-0 shrink-0" ref={anchor} />
      <div
        aria-busy={busy}
        aria-disabled={disabled}
        aria-expanded={open}
        aria-label={label}
        className={
          disabled ? "sticky z-10 shrink-0 cursor-default" : "sticky z-10 shrink-0 cursor-pointer"
        }
        onClick={() => {
          if (!disabled) {
            coordinator.activate(headerKey);
          }
        }}
        onKeyDown={(event) => {
          if (!disabled && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            coordinator.activate(headerKey);
          }
        }}
        ref={element}
        role="button"
        tabIndex={disabled ? -1 : 0}
      >
        {children}
        {/* Each action owns a strip of the trailing edge, over its icon in the header content. */}
        {actions.map((action, actionIndex) => (
          <div
            aria-label={action.label}
            aria-pressed={action.selected}
            className="absolute inset-y-0 cursor-pointer"
            key={actionIndex}
            onClick={(event) => {
              event.stopPropagation();
              onAction?.(actionIndex);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                onAction?.(actionIndex);
              }
            }}
            role="button"
            style={{
              right: actionTrailingInset + (actions.length - 1 - actionIndex) * actionWidth,
              width: actionWidth,
            }}
            tabIndex={0}
          />
        ))}
      </div>
    </>
  );
}
